import Navigation from "@/components/Navigation";
import Breadcrumb from "@/components/Breadcrumb";
import SchemaMarkup from "@/components/SchemaMarkup";
import { Card } from "@/components/ui/card";
import { Helmet } from "react-helmet";
import { createArticleSchema, getCurrentDate } from "@/utils/schemaHelpers";
import HoroscopeContent from "@/components/HoroscopeContent";

const HoroscopoAries = () => {
  const articleSchema = createArticleSchema(
    "Aries: Horóscopo Diario, Características y Compatibilidad con los 12 Signos",
    "Horóscopo diario, semanal y mensual para Aries. Descubre las características del signo Aries y su compatibilidad zodiacal completa.",
    `${window.location.origin}/horoscopo-aries`
  );

  return (
    <>
      <Helmet>
        <title>Aries: Horóscopo Diario, Características y Compatibilidad con los 12 Signos</title>
        <meta name="description" content="Horóscopo diario, semanal y mensual para Aries. Descubre las características del signo Aries y su compatibilidad zodiacal." />
        <meta property="og:title" content="Horóscopo Aries: Predicciones y Compatibilidad" />
        <meta property="og:description" content="Descubre tu horóscopo diario de Aries, características del signo y compatibilidad con todos los signos del zodiaco." />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={getCurrentDate()} />
        <meta property="article:modified_time" content={getCurrentDate()} />
        <link rel="canonical" href={`${window.location.origin}/horoscopo-aries`} />
      </Helmet>

      <SchemaMarkup schema={articleSchema} />
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <Breadcrumb 
              items={[
                { name: "Inicio", url: "/" },
                { name: "Horóscopo", url: "/horoscopo" },
                { name: "Aries" }
              ]}
            />

            <article 
              itemScope 
              itemType="https://schema.org/Article"
              data-zodiac-sign="aries"
              data-element="fire"
              data-modality="cardinal"
            >
              <header className="text-center mb-12">
                <div className="text-6xl mb-4" aria-hidden="true">♈</div>
                <h1 className="text-3xl font-bold mb-4 text-glow" itemProp="headline">
                  Aries: Horóscopo Diario, Características y Compatibilidad con los 12 Signos
                </h1>
                <p className="text-xl text-muted-foreground">
                  <time dateTime="03-21/04-19">21 de Marzo - 19 de Abril</time>
                </p>
                <meta itemProp="datePublished" content={getCurrentDate()} />
                <meta itemProp="dateModified" content={getCurrentDate()} />
              </header>

              <section className="mb-16" itemProp="articleBody">
                <h2 className="text-2xl font-bold mb-6 text-primary">Tu Horóscopo Aries: Predicciones Actualizadas</h2>
                
                <HoroscopeContent sign="aries" signName="Aries" />
              </section>

              <section className="mb-16">
                <h2 className="text-2xl font-bold mb-6 text-secondary">Características Profundas del Signo Aries: El Guerrero Pionero</h2>
                
                <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Los Pilares Astrológicos de Aries</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-bold text-base mb-2 text-primary">🔥 Elemento: Fuego</h4>
                    <p className="text-muted-foreground">Enfoque en la acción, el impulso y la valentía. Aries es el fuego del nacimiento.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-secondary">🚀 Modalidad: Cardinal</h4>
                    <p className="text-muted-foreground">Define la iniciación, el liderazgo y la capacidad de comenzar. El primer paso es su poder.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-accent">♂️ Planeta Regente: Marte</h4>
                    <p className="text-muted-foreground">Dios de la Guerra, la Acción y la Energía. Símbolo de fuerza y determinación.</p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Perfil de Personalidad Aries: Fortalezas y Debilidades</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-bold text-base mb-3 text-primary">✨ Fortalezas</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Valentía:</strong> Coraje excepcional ante desafíos</li>
                      <li>• <strong>Iniciativa:</strong> Capacidad para comenzar sin miedo</li>
                      <li>• <strong>Energía:</strong> Vitalidad y dinamismo inagotables</li>
                      <li>• <strong>Honestidad:</strong> Sinceridad directa y transparente</li>
                      <li>• <strong>Liderazgo:</strong> Habilidad natural para guiar</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-3 text-secondary">⚠️ Debilidades</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Impulsividad:</strong> Actuar antes de pensar</li>
                      <li>• <strong>Impaciencia:</strong> Dificultad para esperar</li>
                      <li>• <strong>Agresividad:</strong> Tendencia a la confrontación</li>
                      <li>• <strong>Egocentrismo:</strong> Enfoque excesivo en sí mismo</li>
                      <li>• <strong>Abandono:</strong> Dificultad para terminar lo iniciado</li>
                    </ul>
                  </div>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-lg font-bold text-primary">Mantra Clave: "Yo Soy"</p>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Aries en el Amor: Pasión Ardiente y Conquista</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Aries ama con la intensidad de una llama recién encendida. Es el cazador romántico, emocionado por la persecución y la conquista. Necesita emoción, desafío y una pareja que mantenga viva la chispa. Su amor es directo, apasionado y sin filtros.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Valora la independencia mutua y respeta a quienes se mantienen firmes ante su fuerza. Aunque puede ser impulsivo, su lealtad es feroz cuando encuentra a alguien que puede seguir su ritmo. El romance para Aries debe ser una aventura constante, llena de espontaneidad y pasión sin límites.
                </p>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Aries en el Trabajo y Finanzas: Emprendimiento y Liderazgo</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Aries destaca en roles de liderazgo, emprendimiento, deportes, fuerzas armadas, cirugía, ingeniería mecánica y cualquier carrera que requiera valentía, decisión rápida y acción directa. Su energía pionera lo hace excelente para iniciar proyectos y abrir caminos nuevos.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Con las finanzas, Aries es audaz y arriesgado. Invierte con confianza, a veces impulsivamente. Gana dinero rápidamente pero puede gastarlo con la misma velocidad. Excelente para negocios que requieren rapidez de decisión, pero debe aprender paciencia para la construcción de riqueza a largo plazo.
                </p>
              </Card>
            </section>

            <section>
                <h2 className="text-2xl font-bold mb-6 text-accent">Compatibilidad de Aries con los Otros Signos Zodiacales</h2>
                
                <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">💚 Tu Match Perfecto</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-base mb-2">♌ Leo y ♐ Sagitario</h4>
                    <p className="text-muted-foreground">
                      Los signos de fuego comparten pasión, entusiasmo y amor por la aventura. Con Leo hay admiración mutua y competencia sana. Con Sagitario, una conexión basada en la libertad, optimismo y exploración compartida.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2">♊ Géminis y ♒ Acuario</h4>
                    <p className="text-muted-foreground">
                      Los signos de aire alimentan el fuego de Aries. Géminis aporta diversión e inteligencia estimulante, mientras que Acuario ofrece visión futurista y la independencia que Aries respeta.
                    </p>
                  </div>
                  </div>
                </Card>

                <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">⚡ Los Desafíos y Lecciones de Aries</h3>
                <p className="text-muted-foreground mb-4">
                  La paciencia y la consideración son los grandes desafíos de Aries. Libra, su signo opuesto, le enseña sobre diplomacia, equilibrio y la importancia de considerar las necesidades ajenas. Los signos de tierra (Tauro, Virgo, Capricornio) le muestran el valor de la persistencia y la construcción gradual.
                </p>
                <p className="text-muted-foreground">
                  Los signos de agua (Cáncer, Escorpio, Piscis) desafían a Aries a profundizar emocionalmente y a entender que la vulnerabilidad no es debilidad. Capricornio le enseña que el verdadero liderazgo incluye responsabilidad a largo plazo, no solo la emoción del inicio.
                  </p>
                </Card>
              </section>
            </article>
          </div>
        </main>
      </div>
    </>
  );
};

export default HoroscopoAries;
